import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EMPTY = { location_id: "", location_name: "", fare: "", days: [], start: "11:00", end: "16:00", weekly: true, date: "", active: true, label: "" };
const fmt12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

const useCall = () => {
  const qc = useQueryClient();
  return async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      qc.invalidateQueries({ queryKey: ["adminPromos"] });
      return true;
    } catch (e) {
      toast.error(formatApiError(e), { duration: 8000 });
      return false;
    }
  };
};

function LocationSearch({ value, onPick }) {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["promoLocSearch", q], queryFn: () => api.get("/admin/locations", { params: { q, active: true } }).then((r) => r.data), enabled: q.length >= 2 });
  if (value.location_id) {
    return <p className="flex items-center justify-between rounded-lg border p-2 text-sm" data-testid="promo-selected-location">{value.location_name}
      {!value.locked && <button type="button" className="text-xs text-primary" onClick={() => onPick({ location_id: "", location_name: "" })}>Change</button>}</p>;
  }
  return (
    <div className="space-y-1">
      <Input placeholder="Search the Scoopz directory" value={q} onChange={(e) => setQ(e.target.value)} data-testid="promo-location-search" />
      {(data?.items || data || []).slice?.(0, 8).map((l) => (
        <button key={l.id} type="button" onClick={() => onPick({ location_id: l.id, location_name: l.name })} data-testid={`promo-location-result-${l.id}`}
          className="block w-full rounded-md px-2 py-1 text-left text-sm hover:bg-muted">{l.name} <span className="text-xs text-muted-foreground">· {l.city}</span></button>
      ))}
    </div>
  );
}

function OfferForm({ initial, onClose }) {
  const call = useCall();
  const [f, setF] = useState(initial);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    const body = { ...f, fare: Number(f.fare), date: f.weekly ? null : f.date || null, label: f.label || null };
    const ok = await call(() => (f.id ? api.put(`/admin/promotions/${f.id}`, body) : api.post("/admin/promotions", body)), f.id ? "Offer updated" : "Offer created");
    if (ok) onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card" data-testid="promo-offer-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit offer" : "New promotional offer"}</DialogTitle></DialogHeader>
        <LocationSearch value={f} onPick={(l) => setF({ ...f, ...l })} />
        <label className="text-xs font-semibold">Flat fare per eligible ride
          <Input type="number" min="1" step="0.5" value={f.fare} onChange={(e) => set("fare", e.target.value)} data-testid="promo-fare-input" /></label>
        <label className="flex items-center gap-2 text-sm">Repeats weekly <Switch checked={f.weekly} onCheckedChange={(v) => set("weekly", v)} data-testid="promo-weekly-switch" /></label>
        {f.weekly ? (
          <div className="flex flex-wrap gap-1.5" data-testid="promo-days">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => set("days", f.days.includes(i) ? f.days.filter((x) => x !== i) : [...f.days, i])} data-testid={`promo-day-${i}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${f.days.includes(i) ? "border-primary bg-primary text-primary-foreground" : "border-white/15"}`}>{d}</button>
            ))}
            <button type="button" className="text-xs text-primary" onClick={() => set("days", [0, 1, 2, 3, 4, 5, 6])} data-testid="promo-every-day">Every day</button>
          </div>
        ) : (
          <Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="promo-date-input" />
        )}
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs font-semibold">Start (Central)<Input type="time" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid="promo-start-input" /></label>
          <label className="text-xs font-semibold">End (Central)<Input type="time" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid="promo-end-input" /></label>
        </div>
        <Input placeholder="Label (optional, e.g. Tuesday lunch)" value={f.label || ""} onChange={(e) => set("label", e.target.value)} data-testid="promo-label-input" />
        <label className="flex items-center gap-2 text-sm">Active <Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="promo-active-switch" /></label>
        <p className="text-[11px] text-muted-foreground">Rides whose pickup OR drop-off is this directory location, with a pickup time inside the window, pay this flat fare. No limit on uses.</p>
        <Button onClick={save} disabled={!f.location_id || !f.fare} data-testid="promo-save-button">Save offer</Button>
      </DialogContent>
    </Dialog>
  );
}

function Offer({ o, onEdit }) {
  const call = useCall();
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-white/5 py-2 text-sm" data-testid={`promo-offer-${o.id}`}>
      <span className="font-display text-lg font-bold text-primary" data-testid={`promo-offer-fare-${o.id}`}>{money(o.fare)}</span>
      <span className="min-w-0 flex-1 text-xs">
        <span className="block font-semibold">{o.weekly ? (o.days.length === 7 ? "Every day" : o.day_names.join(", ")) : `One time · ${o.date}`} · {fmt12(o.start)}–{fmt12(o.end)}</span>
        <span className="text-muted-foreground">{o.weekly ? "Repeats weekly" : "Does not repeat"}{o.label ? ` · ${o.label}` : ""} · {o.live ? "Active" : o.active ? "Ended" : "Inactive"}</span>
      </span>
      <Switch checked={o.active} onCheckedChange={(v) => call(() => api.post(`/admin/promotions/${o.id}/active`, { active: v }), v ? "Offer activated" : "Offer deactivated")} data-testid={`promo-offer-toggle-${o.id}`} />
      <button type="button" onClick={() => onEdit(o)} data-testid={`promo-offer-edit-${o.id}`}><Pencil className="h-4 w-4" /></button>
      <button type="button" onClick={() => window.confirm("Delete this offer?") && call(() => api.delete(`/admin/promotions/${o.id}`), "Offer deleted")} data-testid={`promo-offer-delete-${o.id}`}><Trash2 className="h-4 w-4 text-destructive" /></button>
    </div>
  );
}

function LocationCard({ g, onEdit, onAdd }) {
  const call = useCall();
  return (
    <div className={`rounded-xl border bg-card p-4 ${g.active ? "border-primary/40" : "opacity-70"}`} data-testid={`promo-location-${g.location_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 font-semibold">{g.location_name} <span className="text-xs text-muted-foreground">· {g.offers.length} offer{g.offers.length > 1 ? "s" : ""} · {g.active ? "uses 1 slot" : "inactive"}</span></p>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid={`promo-add-offer-${g.location_id}`}><Plus className="mr-1 h-3 w-3" />Offer</Button>
        <Button size="sm" variant="outline" onClick={() => call(() => api.post(`/admin/promotions/location/${g.location_id}/active`, { active: !g.active }), g.active ? "Location deactivated" : "Location reactivated")}
          data-testid={`promo-location-toggle-${g.location_id}`}><Power className="mr-1 h-3 w-3" />{g.active ? "Deactivate all" : "Reactivate"}</Button>
        <Button size="sm" variant="ghost" onClick={() => window.confirm(`Delete all offers for ${g.location_name}?`) && call(() => api.delete(`/admin/promotions/location/${g.location_id}`), "Location offers deleted")}
          data-testid={`promo-location-delete-${g.location_id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </div>
      {g.offers.map((o) => <Offer key={o.id} o={o} onEdit={onEdit} />)}
    </div>
  );
}

export default function AdminPromotions() {
  const [form, setForm] = useState(null);
  const { data } = useQuery({ queryKey: ["adminPromos"], queryFn: () => api.get("/admin/promotions").then((r) => r.data) });
  const full = data && data.active_locations >= data.limit;
  return (
    <div className="fade-up space-y-5" data-testid="admin-promotions">
      <PageHead title="Promotions" sub="Flat-fare offers tied to directory locations. Weekly offers repeat automatically; uses are unlimited.">
        <Button onClick={() => setForm(EMPTY)} data-testid="promo-new-button"><Plus className="mr-1 h-4 w-4" />New offer</Button>
      </PageHead>
      {data && (
        <div className={`rounded-xl border p-4 ${full ? "border-amber-400/50 bg-amber-400/10" : "bg-card"}`} data-testid="promo-capacity">
          <p className="font-display text-xl font-bold">ACTIVE PROMOTIONAL LOCATIONS: <span data-testid="promo-capacity-count">{data.active_locations} / {data.limit}</span></p>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${(data.active_locations / data.limit) * 100}%` }} /></div>
          {full && <p className="mt-2 text-xs text-amber-200" data-testid="promo-capacity-full">All {data.limit} location slots are occupied. You can still add offers to active locations; deactivate a location to free a slot.</p>}
        </div>
      )}
      {!data?.locations.length && <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">No promotions yet.</p>}
      {data?.locations.map((g) => (
        <LocationCard key={g.location_id} g={g} onEdit={(o) => setForm({ ...o, locked: true })}
          onAdd={() => setForm({ ...EMPTY, location_id: g.location_id, location_name: g.location_name, locked: true })} />
      ))}
      {form && <OfferForm initial={form} onClose={() => setForm(null)} />}
    </div>
  );
}
